import React from 'react';
import { CheckCircle, X } from 'lucide-react';

/**
 * Compact "Sent N mins ago" / "Failed N mins ago" / "Not sent" pill.
 *
 * Props expected on `row`:
 *  - lastEmailedAt: ISO timestamp
 *  - lastEmailedSuccess: bool
 *  - lastEmailedTo: string[]
 *  - lastEmailedBy: string
 *  - lastEmailedError: string
 */
export default function EmailStatusBadge({ row, testIdSuffix = '' }) {
  if (!row?.lastEmailedAt) {
    return (
      <span
        className="text-[11px] text-gray-400 italic"
        data-testid={`email-status-${testIdSuffix || 'na'}`}
      >
        not sent
      </span>
    );
  }
  const sent = new Date(row.lastEmailedAt);
  const now = new Date();
  const diffMs = now - sent;
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const relative =
    minutes < 1
      ? 'just now'
      : minutes < 60
      ? `${minutes}m ago`
      : hours < 24
      ? `${hours}h ago`
      : days < 30
      ? `${days}d ago`
      : sent.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const recipients = (row.lastEmailedTo || []).join(', ');
  const tooltip = row.lastEmailedSuccess
    ? `Sent to ${recipients || 'recipient'} on ${sent.toLocaleString('en-IN')}${
        row.lastEmailedBy ? ` by ${row.lastEmailedBy}` : ''
      }`
    : `Last attempt FAILED on ${sent.toLocaleString('en-IN')}${
        row.lastEmailedError ? ` — ${row.lastEmailedError}` : ''
      }`;
  const cls = row.lastEmailedSuccess
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-rose-50 text-rose-800 border-rose-200';
  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}
      title={tooltip}
      data-testid={`email-status-${testIdSuffix || 'na'}`}
    >
      {row.lastEmailedSuccess ? <CheckCircle size={11} /> : <X size={11} />}
      <span>
        {row.lastEmailedSuccess ? `Sent ${relative}` : `Failed ${relative}`}
      </span>
    </div>
  );
}

export const isUnemailed = (row) =>
  !row?.lastEmailedAt || row?.lastEmailedSuccess === false;
